import { Subscription } from '@content-api/api/subscription/subscription.model';
import { compareSubscriptions } from '@pages/subscription/utils';
import { useSubscriptions } from '@pages/subscription/hooks';
import { Typography, Anchor, toast } from '@ui/components';
import { SubscriptionCard } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { Report } from '@ui/components/icons';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';
import { useState } from 'react';

import { DowngradeModal } from '../downgrade-modal';
import { UpgradeModal } from '../upgrade-modal';

const NUM_OF_LOADING_CARDS = 2;

export const SubscriptionsList = () => {
  const { t } = useTranslation();

  const hasModifyPermission = useHasPermission('SUBSCRIPTIONS_MODIFY', 'WRITE');
  const {
    setBackupSubscription,
    isInitialDataLoading,
    subscriptions,
    initialData,
    isLoading,
    refetch,
  } = useSubscriptions({
    setBackupSubscriptionSuccess: () => {
      toast.success(t('SUBSCRIPTIONS.SUBSCRIPTION_CHANGED_SUCCESSFULLY'));
      refetch();
    },
  });

  const [downgradeModalCode, setDowngradeModalCode] = useState<string | null>(null);
  const [upgradeModalCode, setUpgradeModalCode] = useState<string | null>(null);

  const currentlySelected = initialData?.items.find(
    (subscription) => subscription.code === subscriptions?.selectedSubscriptionCode,
  );

  const onSelect = (subscription: Subscription) => {
    setDowngradeModalCode(null);
    setUpgradeModalCode(null);

    if (!currentlySelected) {
      setBackupSubscription({ subscriptionCode: subscription.code });
      return;
    }

    const mode = compareSubscriptions(currentlySelected, subscription);
    if (mode === 'DOWNGRADE') setDowngradeModalCode(subscription.code);
    if (mode === 'UPGRADE') setUpgradeModalCode(subscription.code);
  };

  if (isLoading || isInitialDataLoading) {
    return (
      <div className="flex gap-6 overflow-auto w-full mt-8">
        {Array.from({ length: NUM_OF_LOADING_CARDS }).map((_, index) => (
          <SubscriptionCard isLoading={true} key={index} />
        ))}
      </div>
    );
  }

  return (
    <section className="h-full w-full overflow-auto mt-8">
      <div className="flex gap-6 w-full">
        {initialData?.items.map((subscription) => (
          <div className="gap-4 pb-4 space-y-4 w-min" key={subscription.id}>
            <SubscriptionCard
              isSelected={subscription.code === subscriptions?.selectedSubscriptionCode}
              isCurrent={subscription.code === subscriptions?.selectedSubscriptionCode}
              numOfEmployees={subscriptions?.numOfEmployees}
              numOfVehicles={subscriptions?.numOfVehicles}
              onSelect={() => onSelect(subscription)}
              disabled={!hasModifyPermission}
              isLoading={isLoading}
              {...subscription}
            />

            {subscription.code === subscriptions?.selectedSubscriptionCode && (
              <div className="flex gap-2 text-weak items-center justify-center">
                <Report className="size-4" />
                <Typography variant="labelSmallBook" className="text-weak">
                  {t('SUBSCRIPTIONS.FEE_IS_SHOWN_ON_INVOICE_INCLUDED')}

                  <Anchor asChild>
                    <Link
                      to={`../${Routes.COST_CENTER}/${subscriptions.defaultCostCenter.id}`}
                      className="ml-1"
                    >
                      {subscriptions.defaultCostCenter.name}
                    </Link>
                  </Anchor>
                </Typography>
              </div>
            )}
          </div>
        ))}

        {initialData?.hasTbaService && (
          <div className="flex flex-col gap-4 pb-4">
            <SubscriptionCard name={t('SUBSCRIPTIONS.SOON')} isLoading={true} />
          </div>
        )}
      </div>

      <UpgradeModal
        onClose={({ upgrade }) => {
          if (upgrade) {
            toast.success(t('SUBSCRIPTIONS.SUBSCRIPTION_CHANGED_SUCCESSFULLY'));
            refetch();
          }
          setUpgradeModalCode(null);
        }}
        subscriptionCode={upgradeModalCode}
      />

      <DowngradeModal
        onClose={({ downgrade }) => {
          if (downgrade) {
            toast.success(t('SUBSCRIPTIONS.SUBSCRIPTION_CHANGED_SUCCESSFULLY'));
            refetch();
          }
          setDowngradeModalCode(null);
        }}
        subscriptionCode={downgradeModalCode}
      />
    </section>
  );
};
